/**
 * Selling, as one flow with stages — and the single place that decides which stage a sale is at.
 *
 * **Why this file exists at all.** The operator's verdict on the module was *"I have a whole lot of
 * stuff that I can't even tell what is what"*, and the largest single cause was that one job —
 * selling a unit to somebody — was told across four screens. A reservation lived on one, the sale
 * it became on another, the money on a third and the handover on a fourth, and the reader carried
 * the thread between them. Item 5 of `14-DEVELOPMENT-MODULE-PLAN.md` settles that it is **one flow
 * with a visible stage**, and this is the arithmetic behind that sentence.
 *
 * **It is deliberately derived rather than stored, and there is no column to add.** Every fact the
 * stage is made of is already a fact of the record — `purchases.status`, whether a unit has been
 * allocated, whether any money has arrived — and a `stage` column would be a second copy of those,
 * free to disagree with them the first time a payment is reversed. Deriving it also means an
 * imported sale, a converted reservation and one typed in at the desk all land on the same answer
 * without anybody remembering to set a field.
 *
 * **One implementation is the whole point.** Item 12 of the plan makes every figure on the Overview
 * a link to the list that produced it, and the operator's words were *"All clickable to filter
 * exactly what was stated."* A figure that counted one way and a list that filtered another is a
 * dashboard nobody believes twice. So the Overview, the Sales list and a sale's own spine all count
 * with `stageOf` and filter with `stageOf`, over the same rows, and cannot disagree by
 * construction — not by two people remembering to keep two copies in step.
 */

/* ---------------------------------------------------------------- the stages */

/**
 * The stages, in the order they happen.
 *
 * The operator named seven chips — All, then these six — and `cancelled` is the seventh member here
 * for a reason given below. The order is the order of the spine: a sale moves down this list and
 * never back up, which is what lets a screen draw "what is done behind and what is next ahead"
 * without a table of transitions.
 */
export const SALE_STAGES = [
  "reserved",
  "terms_agreed",
  "allocated",
  "in_payment",
  "handed_over",
  "defaulted",
  "cancelled",
] as const;

export type SaleStage = (typeof SALE_STAGES)[number];

/**
 * The stages drawn on the spine of one sale.
 *
 * Default and cancellation are ends rather than steps — a sale that reaches either has stopped
 * moving — so they are stages a sale can be *at* and not milestones it passes through. Drawing them
 * on the spine would put two futures on every ordinary sale that will never reach either.
 */
export const SPINE_STAGES: SaleStage[] = ["reserved", "terms_agreed", "allocated", "in_payment", "handed_over"];

/**
 * What each stage is called, and what it means.
 *
 * Plain and formal, per the register rule in `09-CONVENTIONS.md`. Not "what's next", not "waiting
 * on the buyer" — these sit on a screen an estate manager reads in front of a client.
 */
export const STAGE_LABEL: Record<SaleStage, string> = {
  reserved: "Reserved",
  terms_agreed: "Terms agreed",
  allocated: "Allocated",
  in_payment: "In payment",
  handed_over: "Handed over",
  defaulted: "Defaulted",
  cancelled: "Cancelled",
};

/** What has happened at each stage, for the spine, where a label alone is not enough. */
export const STAGE_MEANING: Record<SaleStage, string> = {
  reserved: "A holding fee was taken and the sale has not been agreed.",
  terms_agreed: "A price and a payment plan are on record.",
  allocated: "A particular unit has been assigned to this buyer.",
  in_payment: "Money is arriving against the plan.",
  handed_over: "The unit has been delivered and the sale is closed.",
  defaulted: "The plan has broken down and is awaiting a decision.",
  cancelled: "The sale was withdrawn.",
};

export const STAGE_TONE: Record<SaleStage, "success" | "warn" | "brand" | "muted" | "danger"> = {
  reserved: "warn",
  terms_agreed: "muted",
  allocated: "brand",
  in_payment: "success",
  handed_over: "muted",
  defaulted: "danger",
  cancelled: "danger",
};

export function isSaleStage(value: unknown): value is SaleStage {
  return typeof value === "string" && (SALE_STAGES as readonly string[]).includes(value);
}

/* ------------------------------------------------------------------ the rows */

/** A sale as `GET /purchases` returns it, plus the two fields noted below. */
export interface PurchaseRow {
  id: string;
  reference: string;
  status: string;
  price: number;
  priceLabel: string;
  paid: number;
  paidLabel: string;
  billed: number;
  arrears: number;
  outstanding: number;
  startDate: string;
  allocatedAt: string | null;
  buyerId: string;
  buyerName: string;
  buyerPhone: string | null;
  estateId: string;
  estateName: string;
  plotLabel: string | null;
  /**
   * The next payment falling due, its date and its amount.
   *
   * **The API does not send these yet, and that is recorded here rather than hidden.** The operator
   * asked for both figures on the row — *"the figure you chase and the figure you are owed are
   * different questions"* — and `GET /purchases/:id` computes exactly this as `standing.nextDue`
   * while `GET /purchases` does not. Fetching it per row would be one request per sale on a list of
   * two hundred, which is not a trade worth making, so the column reads "—" until the list endpoint
   * carries it. It is typed optional so the column lights up the moment it does, with no second
   * change here.
   */
  nextDueDate?: string | null;
  nextDueAmount?: number | null;
}

/** A reservation as `GET /reservations` returns it. */
export interface ReservationRow {
  id: string;
  reference: string;
  status: string;
  state: string;
  stateLabel: string;
  reservedOn: string;
  expiresOn: string | null;
  feeAmount: number;
  feeAmountLabel: string;
  paid: number;
  paidLabel: string;
  buyerId: string;
  buyerName: string;
  buyerPhone: string | null;
  estateId: string;
  estateName: string;
  plotId: string | null;
  plotLabel: string | null;
  purchaseId: string | null;
  purchaseReference: string | null;
}

/**
 * One row of the Sales list, whichever record it came from.
 *
 * A reservation and a purchase are different records and are deliberately **not** merged into one
 * table on the server — a reservation has no price and no schedule, and giving it empty ones to fit
 * a shared shape is how a list starts lying. They are merged *here*, where the only thing being
 * shared is a row on a screen.
 */
export interface SaleRow {
  /** `purchase` or `reservation` — which record this row opens. */
  kind: "purchase" | "reservation";
  id: string;
  href: string;
  reference: string;
  stage: SaleStage;
  /** "Reserved · expired", where the record's own state adds something the stage does not say. */
  stageDetail: string | null;
  buyerId: string;
  buyerName: string;
  buyerPhone: string | null;
  estateId: string;
  estateName: string;
  unitLabel: string | null;
  /** Minor units, and null where the record does not carry one — a reservation has no price. */
  price: number | null;
  paid: number;
  outstanding: number;
  arrears: number;
  nextDueDate: string | null;
  nextDueAmount: number | null;
  /** For ordering: the day this record started. */
  startedOn: string;
}

/* ------------------------------------------------------- deriving the stage */

/**
 * Which stage a sale is at.
 *
 * **Read as a ladder, not as a switch.** The tests below it are in reverse order of the spine and
 * the first one that answers wins, because a sale that has been allocated *and* is being paid is at
 * the later of the two — the earlier stages are behind it, not competing with it. Written the other
 * way round, an allocated sale would sit at "Allocated" for four years while the money came in.
 *
 * A plan that is settled but not handed over stays at **In payment**, which is where the spine's one
 * primary action is "Hand over". Calling it something else would need a sixth stage for a state
 * whose only question is when the keys are given.
 */
export function stageOf(purchase: {
  status: string;
  paid: number;
  allocatedAt: string | null;
  plotLabel: string | null;
}): SaleStage {
  if (purchase.status === "cancelled") return "cancelled";
  if (purchase.status === "defaulted") return "defaulted";
  if (purchase.status === "handed_over") return "handed_over";
  if (purchase.paid > 0) return "in_payment";
  /* Allocation is a stage rather than a field — `purchases.plot_id` is deliberately nullable
     because the money can arrive before the unit is chosen, sometimes by ballot. Either the
     timestamp or the unit itself proves it happened; an imported sale carries the second and not
     always the first. */
  if (purchase.allocatedAt || purchase.plotLabel) return "allocated";
  return "terms_agreed";
}

/**
 * A purchase, as a row of the Sales list.
 */
export function saleFromPurchase(row: PurchaseRow): SaleRow {
  const stage = stageOf(row);
  return {
    kind: "purchase",
    id: row.id,
    href: `/app/development/sales/${row.id}`,
    reference: row.reference,
    stage,
    /* Said only where it adds something. A settled plan awaiting handover reads "In payment" and a
       reader is entitled to know there is nothing left to collect. */
    stageDetail: stage === "in_payment" && row.outstanding <= 0 ? "Settled" : null,
    buyerId: row.buyerId,
    buyerName: row.buyerName,
    buyerPhone: row.buyerPhone,
    estateId: row.estateId,
    estateName: row.estateName,
    unitLabel: row.plotLabel,
    price: row.price,
    paid: row.paid,
    outstanding: row.outstanding,
    arrears: row.arrears,
    nextDueDate: row.nextDueDate ?? null,
    nextDueAmount: row.nextDueAmount ?? null,
    startedOn: row.startDate,
  };
}

/**
 * A reservation, as a row of the Sales list.
 *
 * **A converted reservation produces no row.** It became the purchase beside it, and drawing both
 * would count one buyer twice on every figure this list feeds. The reservation is still reachable —
 * the sale's own spine names it — so nothing is lost by leaving it out of the count.
 *
 * A reservation that has run out or been let go still produces a row, at the Reserved stage with its
 * own state said beside it. It is stock somebody is holding, or was, and a list that quietly drops
 * it is a list that loses records.
 */
export function saleFromReservation(row: ReservationRow): SaleRow | null {
  if (row.purchaseId) return null;
  return {
    kind: "reservation",
    id: row.id,
    href: `/app/development/sales/reservations/${row.id}`,
    reference: row.reference,
    stage: "reserved",
    stageDetail: row.state === "held" ? null : row.stateLabel,
    buyerId: row.buyerId,
    buyerName: row.buyerName,
    buyerPhone: row.buyerPhone,
    estateId: row.estateId,
    estateName: row.estateName,
    unitLabel: row.plotLabel,
    /* A reservation has no agreed price. Null rather than zero: nothing has been agreed, which is
       a different fact from "agreed at nothing", and a column that prints one as the other is the
       reason money is never a nullable number pretending to be a total. */
    price: null,
    paid: row.paid,
    outstanding: 0,
    arrears: 0,
    /* The date the hold runs out is the next thing that happens on a reservation, and it is the
       figure a sales desk chases. There is no amount attached to it. */
    nextDueDate: row.expiresOn,
    nextDueAmount: null,
    startedOn: row.reservedOn,
  };
}

/**
 * Every sale, newest first.
 *
 * Sorted here rather than by either endpoint, because the two arrive already sorted among
 * themselves and interleaving them is the whole reason this list exists.
 */
export function mergeSales(purchases: PurchaseRow[], reservations: ReservationRow[]): SaleRow[] {
  const rows = [
    ...purchases.map(saleFromPurchase),
    ...reservations.map(saleFromReservation).filter((row): row is SaleRow => row !== null),
  ];
  return rows.sort((a, b) => (a.startedOn < b.startedOn ? 1 : a.startedOn > b.startedOn ? -1 : 0));
}

/** How many sales sit at each stage. The chips' counts, and the Overview's figures. */
export function countByStage(rows: SaleRow[]): Record<SaleStage, number> {
  const counts = Object.fromEntries(SALE_STAGES.map((stage) => [stage, 0])) as Record<SaleStage, number>;
  for (const row of rows) counts[row.stage] += 1;
  return counts;
}

/* ---------------------------------------------------- the filters behind the figures */

/**
 * Every way the Sales list can be narrowed, expressed once.
 *
 * Item 12 of the plan is explicit that the Overview commits the Sales list to a filter for every
 * state it counts: *"the Sales list must carry filters for every one of those states — stage, due
 * soon, overdue, awaiting allocation, awaiting handover, defaulted"*. They are named here so a card
 * links to `salesLink({ overdue: true })` rather than to a hand-typed query string that can be
 * mistyped into a list nobody notices is wrong.
 */
export interface SalesFilter {
  stage?: SaleStage | null;
  /** Something is overdue on the plan. */
  overdue?: boolean;
  /**
   * A **sale** with no unit against it — the thing "Not yet allocated" means.
   *
   * Purchases only. A hold with no plot against it is somebody reserving *into the scheme*, which
   * is an ordinary thing to do and is not a sale waiting to be allocated — counting it would make
   * "awaiting allocation" a number nobody could act on, because half of it would be answered by
   * converting rather than by choosing a unit.
   */
  unallocated?: boolean;
  /** Settled in full and not yet handed over. */
  awaitingHandover?: boolean;
  /** A reservation whose hold has run out, or runs out within the week. */
  lapsing?: boolean;
  /**
   * Only sales that are actually sales — a purchase record, not a hold.
   *
   * It exists because of the rule the Overview lives by: a figure must open **exactly** the rows it
   * counted. "Sold" counts purchases, and an unfiltered Sales list also carries every reservation,
   * so without this the figure said one and the list showed two. Naming the filter is the fix;
   * making the figure link to a list it does not match is not.
   */
  sold?: boolean;
  /** Confined to one Property. */
  estateId?: string | null;
  /** Confined to one buyer. */
  buyerId?: string | null;
}

/** Does this row belong in a list narrowed that way? */
export function matchesFilter(row: SaleRow, filter: SalesFilter, today: string): boolean {
  if (filter.stage && row.stage !== filter.stage) return false;
  if (filter.estateId && row.estateId !== filter.estateId) return false;
  if (filter.buyerId && row.buyerId !== filter.buyerId) return false;
  if (filter.sold && (row.kind !== "purchase" || row.stage === "cancelled")) return false;
  if (filter.overdue && row.arrears <= 0) return false;
  if (filter.unallocated && (row.kind !== "purchase" || row.unitLabel !== null || row.stage === "cancelled")) {
    return false;
  }
  if (filter.awaitingHandover) {
    const settled = row.kind === "purchase" && row.outstanding <= 0 && row.stage === "in_payment";
    if (!settled) return false;
  }
  if (filter.lapsing) {
    if (row.kind !== "reservation" || row.stage !== "reserved") return false;
    if (!row.nextDueDate) return false;
    /* Within the week, expired included. A hold that ran out yesterday is the more urgent of the
       two and a filter that showed only future ones would hide it. */
    if (row.nextDueDate > addDays(today, 7)) return false;
  }
  return true;
}

export function filterSales(rows: SaleRow[], filter: SalesFilter, today: string): SaleRow[] {
  return rows.filter((row) => matchesFilter(row, filter, today));
}

/**
 * The address of a filtered Sales list.
 *
 * The one place a link to Sales is built, so a figure and the list it opens carry the same
 * narrowing. Nothing outside this file types `?stage=`.
 */
export function salesLink(filter: SalesFilter): string {
  const params = new URLSearchParams();
  if (filter.stage) params.set("stage", filter.stage);
  if (filter.sold) params.set("sold", "1");
  if (filter.overdue) params.set("overdue", "1");
  if (filter.unallocated) params.set("unallocated", "1");
  if (filter.awaitingHandover) params.set("awaiting", "handover");
  if (filter.lapsing) params.set("lapsing", "1");
  if (filter.estateId) params.set("estateId", filter.estateId);
  if (filter.buyerId) params.set("buyerId", filter.buyerId);
  const query = params.toString();
  return query ? `/app/development/sales?${query}` : "/app/development/sales";
}

/** The other direction: what a Sales address is asking for. */
export function filterFromParams(params: URLSearchParams): SalesFilter {
  const stage = params.get("stage");
  return {
    stage: isSaleStage(stage) ? stage : null,
    sold: params.get("sold") === "1",
    overdue: params.get("overdue") === "1",
    unallocated: params.get("unallocated") === "1",
    awaitingHandover: params.get("awaiting") === "handover",
    lapsing: params.get("lapsing") === "1",
    estateId: params.get("estateId"),
    buyerId: params.get("buyerId"),
  };
}

/**
 * A day-date, moved on.
 *
 * `YYYY-MM-DD` throughout — a day that means a day is a string and never a `Date`, ADR-0003 — and
 * the arithmetic goes through UTC deliberately: constructing a local `Date` from a day string and
 * adding to it lands on the wrong day for anybody east or west of the machine that ran it.
 */
export function addDays(day: string, days: number): string {
  const at = new Date(`${day}T00:00:00Z`);
  at.setUTCDate(at.getUTCDate() + days);
  return at.toISOString().slice(0, 10);
}

/* ----------------------------------------------------------------- the spine */

/** One step of a sale's spine, ready to draw. */
export interface SpineStep {
  stage: SaleStage;
  label: string;
  meaning: string;
  /** Behind the reader: this has happened. */
  done: boolean;
  /** Where the sale is standing now. */
  current: boolean;
}

/**
 * A sale's spine — what is done behind, and what is next ahead.
 *
 * **A sale may start at any stage**, on the operator's instruction: walk-in buyers exist, and
 * forcing a reservation that is converted a second later is paperwork invented by the software. So
 * a sale entered at the desk begins at Terms agreed and the Reserved step is simply not drawn —
 * rather than drawn as an unfinished step it will never reach, which would make every ordinary sale
 * look incomplete for its whole life.
 */
export function spineFor(stage: SaleStage, opts: { reserved: boolean }): SpineStep[] {
  const steps = SPINE_STAGES.filter((step) => step !== "reserved" || opts.reserved);
  const at = steps.indexOf(stage);
  return steps.map((step, index) => ({
    stage: step,
    label: STAGE_LABEL[step],
    meaning: STAGE_MEANING[step],
    /* An ended sale — defaulted or cancelled — is at no step on the spine, and `at` is -1. Every
       step then reads as neither done nor current, which is the honest drawing: the sale stopped,
       and the screen says where it stopped in its own words above the spine. */
    done: at >= 0 && index < at,
    current: index === at,
  }));
}

/**
 * The one action a sale is waiting for, and nothing else.
 *
 * **A row of six buttons of equal weight is precisely what makes the present version read as a
 * database rather than as a job** — the plan says so in those words. So this returns the single
 * primary action for the stage the sale is at; everything else a screen offers is secondary and is
 * drawn as such.
 */
export function primaryActionFor(stage: SaleStage, opts: { settled: boolean }): {
  key: "convert" | "allocate" | "record_payment" | "handover" | null;
  label: string;
  hint: string;
} {
  switch (stage) {
    case "reserved":
      return { key: "convert", label: "Convert to a sale", hint: "Agree a price and a payment plan against this hold." };
    case "terms_agreed":
      return { key: "allocate", label: "Allocate", hint: "Assign a particular unit to this buyer." };
    case "allocated":
      return { key: "record_payment", label: "Record a payment", hint: "Money received against the plan." };
    case "in_payment":
      return opts.settled
        ? { key: "handover", label: "Record handover", hint: "The plan is settled. Handing over is an act with a date of its own." }
        : { key: "record_payment", label: "Record a payment", hint: "Money received against the plan." };
    default:
      return { key: null, label: "", hint: "" };
  }
}

